/**
 * COMPONENT: FormSubmissionsSection
 *
 * Affiche les données des formulaires soumis pour un contact et permet de
 * les modifier inline (V5 R12 — recadrage Desertours : besoin de cocher /
 * décocher des cases comme "abonné newsletter" depuis la fiche contact).
 *
 * Architecture (Option A) : `form_submissions.data` est un JSONB
 * `{ [field_id]: value }`. L'édition lit-modifie-écrit la colonne `data`.
 * RLS : superadmin/admin only via `form_submissions_superadmin_admin_all`.
 */

import { ChevronDown, ChevronRight, FileText, Loader2, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { useContactFormSubmissions } from '../../hooks/useContactFormSubmissions';
import { useFormSubmissionMutations } from '../../hooks/useFormSubmissionMutations';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate } from '../../lib/date-utils';
import type { FormFieldType } from '../../types/form';
import { Badge, toast } from '../ui';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Textarea } from '../ui/textarea';

interface FormSubmissionsSectionProps {
  contactId: string;
}

/**
 * Formate une valeur de champ en lecture seule.
 */
function formatFieldValue(value: unknown, fieldType: FormFieldType): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (value === 'true' || value === 'false') return value === 'true' ? 'Oui' : 'Non';
  if (fieldType === 'date' && typeof value === 'string') {
    return formatDate(value);
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
}

/**
 * Coerce une valeur JSONB hétérogène vers `boolean` pour l'édition checkbox.
 * Tolère true/false, "true"/"false", "Oui"/"Non".
 */
function coerceCheckbox(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const v = value.toLowerCase().trim();
    return v === 'true' || v === 'oui' || v === 'yes' || v === '1';
  }
  return false;
}

/**
 * Coerce une valeur JSONB hétérogène vers `string` pour les inputs texte.
 */
function coerceString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

interface FieldEditorProps {
  fieldId: string;
  fieldType: FormFieldType;
  value: unknown;
  options?: string[];
  required?: boolean;
  onChange: (newValue: unknown) => void;
}

/**
 * Sélectionne le bon contrôle d'édition selon `field_type`.
 */
function FieldEditor({ fieldId, fieldType, value, options, onChange }: FieldEditorProps) {
  const inputId = `field-${fieldId}`;

  switch (fieldType) {
    case 'checkbox':
      return <Checkbox checked={coerceCheckbox(value)} onChange={(checked) => onChange(checked)} />;

    case 'textarea':
      return (
        <Textarea
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          value={coerceString(value)}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      );

    case 'select':
      return (
        <Select
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          value={coerceString(value)}
          onChange={(e) => onChange(e.target.value)}
          options={(options ?? []).map((opt) => ({ value: opt, label: opt }))}
          placeholder="-- Choisir --"
        />
      );

    case 'radio':
      return (
        <div role="radiogroup" className="flex flex-col gap-1">
          {(options ?? []).map((opt) => (
            <label key={opt} className="inline-flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={inputId}
                value={opt}
                checked={coerceString(value) === opt}
                onChange={() => onChange(opt)}
                data-testid={`field-radio-${fieldId}-${opt}`}
              />
              {opt}
            </label>
          ))}
        </div>
      );

    case 'date':
      return (
        <Input
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          type="date"
          value={coerceString(value).slice(0, 10)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'number':
      return (
        <Input
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          type="number"
          value={coerceString(value)}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number(v));
          }}
        />
      );

    case 'email':
      return (
        <Input
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          type="email"
          value={coerceString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'phone':
      return (
        <Input
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          type="tel"
          value={coerceString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'text':
    default:
      return (
        <Input
          id={inputId}
          data-testid={`field-input-${fieldId}`}
          type="text"
          value={coerceString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

export function FormSubmissionsSection({ contactId }: FormSubmissionsSectionProps) {
  const { submissions, loading, refresh } = useContactFormSubmissions(contactId);
  const { updateFormSubmissionFields, saving } = useFormSubmissionMutations();
  const { role } = usePermissions();
  const canEdit = role === 'admin' || role === 'superadmin';

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Patch local en cours d'édition pour la submission active */
  const [draftPatch, setDraftPatch] = useState<Record<string, unknown>>({});

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const startEditing = (submissionId: string) => {
    setEditingId(submissionId);
    setDraftPatch({});
    setExpandedIds((prev) => new Set(prev).add(submissionId));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftPatch({});
  };

  const handleFieldChange = (fieldId: string, newValue: unknown) => {
    setDraftPatch((prev) => ({ ...prev, [fieldId]: newValue }));
  };

  const saveChanges = async (submissionId: string) => {
    if (Object.keys(draftPatch).length === 0) {
      cancelEditing();
      return;
    }
    try {
      await updateFormSubmissionFields(submissionId, draftPatch);
      toast.success('Champs mis à jour');
      setEditingId(null);
      setDraftPatch({});
      await refresh();
    } catch (err) {
      console.error('updateFormSubmission failed', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Échec de la mise à jour : ${msg}`);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-zinc-900 mb-4">Données de formulaire</h2>
        <div className="animate-pulse space-y-3">
          <div className="h-12 bg-zinc-100 rounded-lg" />
          <div className="h-12 bg-zinc-100 rounded-lg" />
        </div>
      </Card>
    );
  }

  if (submissions.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-zinc-900 mb-4">Données de formulaire</h2>
        <p className="text-sm text-zinc-500">Aucune soumission de formulaire</p>
      </Card>
    );
  }

  const isExpanded = (id: string) => expandedIds.has(id);

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-zinc-900 mb-4">Données de formulaire</h2>
      <div className="space-y-3">
        {submissions.map((sub) => {
          const isEditing = editingId === sub.id;

          return (
            <div
              key={sub.id}
              data-testid={`form-submission-${sub.id}`}
              className="border border-zinc-200 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleExpanded(sub.id)}
                className="flex items-center justify-between w-full p-3 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  {isExpanded(sub.id) ? (
                    <ChevronDown className="size-4 text-zinc-500" />
                  ) : (
                    <ChevronRight className="size-4 text-zinc-500" />
                  )}
                  <FileText className="size-4 text-zinc-500" />
                  <span className="font-medium text-zinc-900">{sub.form_name}</span>
                  {sub.bpi_code && (
                    <Badge variant="secondary" className="ml-2">
                      {sub.bpi_code}
                    </Badge>
                  )}
                </div>
                <span className="text-sm text-zinc-500">{formatDate(sub.created_at)}</span>
              </button>

              {isExpanded(sub.id) && (
                <div className="p-4 border-t border-zinc-200 bg-white">
                  {sub.fields.length === 0 ? (
                    <p className="text-sm text-zinc-500">Aucun champ configuré</p>
                  ) : (
                    <>
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                        {sub.fields.map((field) => {
                          const draftValue = Object.prototype.hasOwnProperty.call(
                            draftPatch,
                            field.field_id
                          )
                            ? draftPatch[field.field_id]
                            : field.value;

                          return (
                            <div key={field.field_id}>
                              <dt className="text-sm text-zinc-500">{field.label}</dt>
                              <dd className="text-sm font-medium text-zinc-900 mt-1">
                                {isEditing ? (
                                  <FieldEditor
                                    fieldId={field.field_id}
                                    fieldType={field.field_type}
                                    value={draftValue}
                                    options={field.options}
                                    onChange={(v) => handleFieldChange(field.field_id, v)}
                                  />
                                ) : (
                                  formatFieldValue(field.value, field.field_type)
                                )}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>

                      {canEdit && (
                        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-zinc-100">
                          {isEditing ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditing}
                                disabled={saving}
                                data-testid={`form-submission-cancel-${sub.id}`}
                              >
                                <X className="size-4" />
                                Annuler
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => saveChanges(sub.id)}
                                disabled={saving}
                                data-testid={`form-submission-save-${sub.id}`}
                              >
                                {saving && <Loader2 className="size-4 animate-spin" />}
                                Sauvegarder
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditing(sub.id)}
                              data-testid={`form-submission-edit-${sub.id}`}
                            >
                              <Pencil className="size-4" />
                              Modifier
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
